import React, { useEffect, useRef, useState } from "react";

import qualitySelector from "@silvermine/videojs-quality-selector";
import classNames from "classnames";
import PropTypes from "prop-types";
import videojs from "video.js";

import useWindowDimensions from "../../hooks/useWindowDimensions";

import "./video.scss";

const Video = ({
  asBackground = false,
  options = {},
  onReady = () => {},
  sources,
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  const { width } = useWindowDimensions();
  const [quality, setQuality] = useState();

  useEffect(() => {
    if (width === 0 || (!sources.xs && !sources.md && !sources.xl)) {
      return;
    }

    let res = sources.xs ? "SD" : undefined;

    if (width >= 1200 && sources.xl) {
      res = "FHD";
    } else if (width >= 768 && sources.md) {
      res = "HD";
    }

    setQuality(res);
  }, [width]);

  useEffect(() => {
    if (!playerRef.current || (!sources.xs && !sources.md && !sources.xl)) return;

    const tmpSources = [
      sources.xs && { label: "SD", selected: quality === "SD", ...sources.xs },
      sources.md && { label: "HD", selected: quality === "HD", ...sources.md },
      sources.xl && { label: "Full HD", selected: quality === "FHD", ...sources.xl },
    ].filter((item) => item);

    playerRef.current.src(tmpSources);
  }, [quality]);

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement("video-js");

      videoRef.current.appendChild(videoElement);

      qualitySelector(videojs);

      const player = videojs(videoElement, options, () => {
        onReady(player);
      });
      player.controlBar.addChild("QualitySelector");
      playerRef.current = player;
    }
  }, [options, videoRef]);

  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <div
      data-vjs-player
      className={classNames(
        "video-player",
        { "video-player--background": asBackground },
      )}
      ref={videoRef}
    />
  );
};

Video.propTypes = {
  asBackground: PropTypes.bool,
  onReady: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.object]),
  sources: PropTypes.shape({
    md: PropTypes.shape({
      src: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
    xl: PropTypes.shape({
      src: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
    xs: PropTypes.shape({
      src: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export default Video;
